import React, { useCallback, useEffect, useState } from 'react';
import {
  Building2, Plus, Pencil, Trash2, X, AlertCircle, MapPin,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import type { EmpFinca } from '../../types/empacadora';
import {
  inputCls, selectCls, labelCls, btnPrimary, btnSecondary, btnDanger, errorCls,
} from '../../components/ui';
import ReactDOM from 'react-dom';

// ─── Modal ────────────────────────────────────────────────────────────────────

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="relative w-full rounded-xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxWidth: '32rem', background: 'var(--surface-overlay)', border: '1px solid var(--line)', maxHeight: '92vh' }}>
        <div className="flex items-center justify-between px-5 py-3.5 shrink-0"
          style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
          <span className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{title}</span>
          <button onClick={onClose} style={{ color: 'var(--ink-faint)' }}><X size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}

// ─── Formulario Finca ─────────────────────────────────────────────────────────

function FincaForm({ initial, onSave, onCancel, empresaId }: {
  initial?: EmpFinca;
  onSave: (data: Partial<EmpFinca>) => Promise<void>;
  onCancel: () => void;
  empresaId: number;
}) {
  const [form, setForm] = useState({
    nombre: initial?.nombre ?? '',
    codigo: initial?.codigo ?? '',
    ubicacion: initial?.ubicacion ?? '',
    tipo_finca: initial?.tipo_finca ?? 'propia',
    area_ha_perimetro: initial?.area_ha_perimetro as number | undefined,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: keyof typeof form, v: any) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.nombre.trim()) { setErr('El nombre es obligatorio.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({ ...form, nombre: form.nombre.trim(), empresa_id: empresaId, activo: true });
    } catch (ex: any) {
      setErr(ex?.message ?? 'Error al guardar.');
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Nombre *</label>
          <input className={inputCls} value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Finca La Lucha" />
        </div>
        <div>
          <label className={labelCls}>Código</label>
          <input className={inputCls} value={form.codigo} onChange={e => set('codigo', e.target.value)} placeholder="FL-01" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Tipo</label>
          <select className={selectCls} value={form.tipo_finca} onChange={e => set('tipo_finca', e.target.value)}>
            <option value="propia">Propia</option>
            <option value="alquilada">Alquilada</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Área total (ha)</label>
          <input className={inputCls} type="number" step="0.01" min="0"
            value={form.area_ha_perimetro ?? ''}
            onChange={e => set('area_ha_perimetro', e.target.value ? parseFloat(e.target.value) : undefined)}
            placeholder="0.00" />
        </div>
      </div>
      <div>
        <label className={labelCls}>Ubicación</label>
        <input className={inputCls} value={form.ubicacion} onChange={e => set('ubicacion', e.target.value)} placeholder="Cantón, provincia" />
      </div>
      {err && <p className={errorCls}>{err}</p>}
      <div className="flex justify-end gap-3 pt-1">
        <button type="button" className={btnSecondary} onClick={onCancel}>Cancelar</button>
        <button type="submit" className={btnPrimary} disabled={saving}>
          {saving ? 'Guardando...' : initial ? 'Actualizar' : 'Crear Finca'}
        </button>
      </div>
    </form>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export function FincasPage() {
  const empresaId = useEmpresaId();
  const [fincas, setFincas] = useState<EmpFinca[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'create' | EmpFinca | null>(null);
  const [toDelete, setToDelete] = useState<EmpFinca | null>(null);
  const [err, setErr] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('emp_fincas')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('activo', true)
      .order('nombre');
    if (error) setErr(error.message);
    else setFincas(data ?? []);
    setLoading(false);
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  async function guardar(data: Partial<EmpFinca>) {
    if (modal && modal !== 'create') {
      const { error } = await supabase.from('emp_fincas').update(data).eq('id', (modal as EmpFinca).id);
      if (error) throw error;
    } else {
      const { error } = await supabase.from('emp_fincas').insert(data);
      if (error) throw error;
    }
    setModal(null);
    await cargar();
  }

  async function eliminar() {
    if (!toDelete) return;
    await supabase.from('emp_fincas').update({ activo: false }).eq('id', toDelete.id);
    setToDelete(null);
    await cargar();
  }

  function fmtHa(v?: number | null) {
    if (v == null) return '—';
    return `${v.toFixed(2)} ha`;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--ink)' }}>Fincas</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--ink-faint)' }}>
            Configuración de fincas de la empresa
          </p>
        </div>
        <button
          onClick={() => setModal('create')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: 'var(--emp-accent)', color: '#fff' }}>
          <Plus size={15} /> Nueva Finca
        </button>
      </div>

      {err && (
        <div className="flex gap-2 p-3 rounded-lg" style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
          <AlertCircle size={15} style={{ color: 'var(--tone-warn-text)', flexShrink: 0 }} />
          <p className="text-sm" style={{ color: 'var(--tone-warn-ink)' }}>{err}</p>
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <p className="text-sm text-center py-10" style={{ color: 'var(--ink-faint)' }}>Cargando...</p>
      ) : fincas.length === 0 ? (
        <div className="text-center py-16 rounded-xl" style={{ border: '1px dashed var(--line)' }}>
          <Building2 size={40} style={{ color: 'var(--ink-faint)', opacity: 0.3, margin: '0 auto 12px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--ink-faint)' }}>No hay fincas registradas</p>
          <p className="text-xs mt-1" style={{ color: 'var(--ink-faint)' }}>Creá la primera con el botón de arriba</p>
        </div>
      ) : (
        <div className="space-y-3">
          {fincas.map(f => (
            <div key={f.id} className="flex items-center gap-4 px-5 py-4 rounded-xl"
              style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
              <Building2 size={18} style={{ color: 'var(--emp-accent-txt)', flexShrink: 0 }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{f.nombre}</span>
                  {f.codigo && (
                    <span className="font-mono text-[10px] px-1.5 py-0.5 rounded"
                      style={{ background: 'var(--emp-accent-bg)', color: 'var(--emp-accent-txt)', border: '1px solid var(--emp-accent)44' }}>
                      {f.codigo}
                    </span>
                  )}
                  <span className="text-[10px] px-1.5 py-0.5 rounded capitalize"
                    style={{ background: 'var(--surface-overlay)', color: 'var(--ink-faint)', border: '1px solid var(--line-dim)' }}>
                    {f.tipo_finca ?? 'propia'}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                  {f.area_ha_perimetro != null && (
                    <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{fmtHa(f.area_ha_perimetro)}</span>
                  )}
                  {f.ubicacion && (
                    <span className="text-[11px] flex items-center gap-1" style={{ color: 'var(--ink-faint)' }}>
                      <MapPin size={10} />{f.ubicacion}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setModal(f)} className="p-1.5 rounded"
                  style={{ color: 'var(--ink-faint)' }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'var(--emp-accent-txt)')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
                  <Pencil size={14} />
                </button>
                <button onClick={() => setToDelete(f)} className="p-1.5 rounded"
                  style={{ color: 'var(--ink-faint)' }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal crear/editar */}
      {modal !== null && (
        <Modal
          title={modal === 'create' ? 'Nueva Finca' : `Editar — ${(modal as EmpFinca).nombre}`}
          onClose={() => setModal(null)}>
          <FincaForm
            initial={modal !== 'create' ? modal as EmpFinca : undefined}
            empresaId={empresaId}
            onSave={guardar}
            onCancel={() => setModal(null)}
          />
        </Modal>
      )}

      {/* Confirm eliminar */}
      {toDelete && (
        <Modal title="Desactivar Finca" onClose={() => setToDelete(null)}>
          <div className="space-y-4">
            <div className="flex gap-3 p-3 rounded" style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
              <AlertCircle size={16} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
              <p className="text-sm" style={{ color: 'var(--tone-warn-ink)' }}>
                ¿Desactivar <strong>{toDelete.nombre}</strong>? Los lotes y bloques quedan intactos.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button className={btnSecondary} onClick={() => setToDelete(null)}>Cancelar</button>
              <button className={btnDanger} onClick={eliminar}>Desactivar</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
